const SITE_DOMAIN = typeof import.meta !== "undefined" && (import.meta as Record<string, unknown>).env ? ((import.meta as Record<string, Record<string, string>>).env.VITE_SITE_URL ?? "https://sugargoospreadsheet.delivery") : "https://sugargoospreadsheet.delivery";

interface WebSiteSchemaProps {
  siteName: string;
  siteUrl: string;
  searchUrlTemplate?: string;
}

export function WebSiteSchema({ siteName, siteUrl, searchUrlTemplate }: WebSiteSchemaProps) {
  const json = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: siteName,
    url: siteUrl,
    potentialAction: searchUrlTemplate
      ? {
          "@type": "SearchAction",
          target: {
            "@type": "EntryPoint",
            urlTemplate: searchUrlTemplate,
          },
          "query-input": "required name=search_term_string",
        }
      : undefined,
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(json) }}
    />
  );
}

interface ArticleSchemaProps {
  headline: string;
  description: string;
  url: string;
  datePublished: string;
  dateModified: string;
  authorName: string;
  authorUrl?: string;
  publisherName: string;
  publisherLogoUrl?: string;
  imageUrl?: string;
}

export function ArticleSchema({
  headline,
  description,
  url,
  datePublished,
  dateModified,
  authorName,
  authorUrl,
  publisherName,
  publisherLogoUrl,
  imageUrl,
}: ArticleSchemaProps) {
  const json: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline,
    description,
    url,
    datePublished,
    dateModified,
    author: {
      "@type": "Person",
      name: authorName,
    },
    publisher: {
      "@type": "Organization",
      name: publisherName,
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": url,
    },
  };

  if (authorUrl) {
    (json.author as Record<string, unknown>).url = authorUrl;
  }
  if (publisherLogoUrl) {
    (json.publisher as Record<string, unknown>).logo = {
      "@type": "ImageObject",
      url: publisherLogoUrl,
    };
  }
  if (imageUrl) {
    json.image = imageUrl;
  }

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(json) }}
    />
  );
}

interface FAQPageSchemaProps {
  questions: Array<{ question: string; answer: string }>;
  url: string;
}

export function FAQPageSchema({ questions, url }: FAQPageSchemaProps) {
  const json = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    url,
    mainEntity: questions.map((q) => ({
      "@type": "Question",
      name: q.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: q.answer,
      },
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(json) }}
    />
  );
}

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface BreadcrumbSchemaProps {
  items: BreadcrumbItem[];
}

export function BreadcrumbSchema({ items }: BreadcrumbSchemaProps) {
  const json = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      name: item.name,
      item: item.url,
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(json) }}
    />
  );
}